import { useEffect, useState } from "react";
import { Modal, Input, Button, Checkbox, List, Tag, Empty, Spin, Alert, message } from "antd";
import { MailOutlined, BankOutlined } from "@ant-design/icons";
import { ApiService } from "api/api.service";

interface PaynoteFundingSource {
  type?: "savings" | "checking";
  status: string;
  bank: string;
  source_id: string;
  lbacc: string;
}

interface ImportPaynoteViaEmailButtonProps {
  ref_id: string;
  refetch: () => void;
  type: "Application" | "Creditor";
}

export default function ImportPaynoteButton({ ref_id, refetch, type }: ImportPaynoteViaEmailButtonProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);

  const {
    data: lookupRes,
    isFetching: isLookingUp,
    error: lookupError,
  } = ApiService.application.useGetPaynoteFundingSourcesViaEmail(submittedEmail);

  const paynotes: PaynoteFundingSource[] | undefined = lookupRes?.result;

  useEffect(() => {
    if (paynotes) {
      setSelected(paynotes.map((p) => p.source_id));
    }
  }, [lookupRes]);

  useEffect(() => {
    if (lookupError) {
      message.error("Failed to look up paynotes for this email");
    }
  }, [lookupError]);

  const importMutation = ApiService.application.useImportPaynote();

  function resetAndClose() {
    setOpen(false);
    setEmail("");
    setSubmittedEmail(undefined);
    setSelected([]);
  }

  function handleImport() {
    if (!submittedEmail) return;
    importMutation.mutate(
      { ref_id, source_ids: selected, email: submittedEmail, type },
      {
        onSuccess: (data) => {
          message.success(data.message);
          resetAndClose();
          refetch();
        },
      },
    );
  }

  function toggleSelected(source_id: string, checked: boolean) {
    setSelected((prev) => (checked ? [...prev, source_id] : prev.filter((id) => id !== source_id)));
  }

  function toggleSelectAll(checked: boolean) {
    setSelected(checked ? (paynotes ?? []).map((p) => p.source_id) : []);
  }

  const allSelected = !!paynotes?.length && selected.length === paynotes.length;

  return (
    <>
      <div className="flex items-center justify-center">
        <Button icon={<MailOutlined />} onClick={() => setOpen(true)}>
          Import Paynote via Email
        </Button>
      </div>

      <Modal title="Import Paynote via Email" open={open} onCancel={resetAndClose} footer={null} destroyOnClose>
        {!submittedEmail ? (
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onPressEnter={() => email && setSubmittedEmail(email)}
              type="email"
            />
            <Button type="primary" disabled={!email} onClick={() => setSubmittedEmail(email)}>
              Find
            </Button>
          </div>
        ) : isLookingUp ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : !paynotes || paynotes.length === 0 ? (
          <Empty description="No payment methods found for this email" />
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              banner
              style={{ marginBottom: 16, borderRadius: 6 }}
              message="Payment methods that exactly match ones already saved to this application will be skipped automatically while importing."
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <Checkbox checked={allSelected} onChange={(e) => toggleSelectAll(e.target.checked)}>
                Select all
              </Checkbox>
              <span style={{ color: "rgba(0,0,0,0.45)", fontSize: 13 }}>
                {selected.length} of {paynotes.length} selected
              </span>
            </div>

            <List
              bordered
              style={{ borderRadius: 8, maxHeight: 320, overflowY: "auto" }}
              dataSource={paynotes}
              renderItem={(p) => (
                <List.Item
                  onClick={() => toggleSelected(p.source_id, !selected.includes(p.source_id))}
                  style={{ cursor: "pointer", padding: "12px 16px" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
                    <Checkbox
                      checked={selected.includes(p.source_id)}
                      onChange={(e) => toggleSelected(p.source_id, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: "rgba(89, 63, 226, 0.08)",
                        color: "#593FE2",
                        flexShrink: 0,
                      }}
                    >
                      <BankOutlined style={{ fontSize: 16 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.bank} •••• {p.lbacc}
                      </div>
                      {p.type && (
                        <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 12, textTransform: "capitalize" }}>
                          {p.type}
                        </div>
                      )}
                    </div>
                    <Tag
                      color={p.status === "active" ? "success" : "default"}
                      style={{ margin: 0, textTransform: "capitalize" }}
                    >
                      {p.status}
                    </Tag>
                  </div>
                </List.Item>
              )}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Button onClick={resetAndClose}>Cancel</Button>
              <Button
                type="primary"
                disabled={selected.length === 0}
                loading={importMutation.isPending}
                onClick={handleImport}
              >
                Import {selected.length > 0 ? `(${selected.length})` : ""}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
